import { GridCellProps } from '@progress/kendo-react-grid';
import { useInternationalization } from '@progress/kendo-react-intl';
import { FocusEvent, SyntheticEvent, useCallback, useRef } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import NumberStepper from 'src/components/design-system/NumberStepper';
import { useContextSelector } from 'use-context-selector';
import { ShoppingCartContext } from '../ShoppingCartContext';

// Same bounds as NumberStepper
const QTY_MAX = 9999;
const QTY_MIN = -9999;

/** How long the qty must stay unchanged (typing or +/−) before it is saved. */
export const QTY_AUTO_SAVE_DELAY_MS = 1000;

/** A half-typed value ("", "-", ".") is not a quantity yet; blur saves whatever it settles on. */
const isIncompleteInput = (text: string) => /^-?\.?$/.test(text.trim());

const clampQty = (qty: number) => Math.min(QTY_MAX, Math.max(QTY_MIN, qty));

const ShoppingCartQtyCell = (props: GridCellProps) => {
  const field = props.field || '';
  const value = props.dataItem[field] ?? 0;
  const { dataItem, dataIndex, onChange } = props;
  const intl = useInternationalization();
  const stepperRef = useRef<HTMLDivElement>(null);
  const saveQuantity = useContextSelector(ShoppingCartContext, (context) => context.saveQuantity);

  // NumberStepper fires this once the qty has stayed unchanged for QTY_AUTO_SAVE_DELAY_MS, so
  // continuous typing or clicking restarts the wait and only the settled value is saved.
  const handleQuantityChange = useCallback(
    (e: SyntheticEvent, newValue: number) => {
      onChange?.({
        dataIndex,
        dataItem,
        field,
        syntheticEvent: e,
        value: newValue,
      });

      const input = stepperRef.current?.querySelector('input');
      if (input && document.activeElement === input && isIncompleteInput(input.value)) {
        return;
      }
      saveQuantity(String(dataItem.productKey), clampQty(newValue));
    },
    [dataIndex, dataItem, field, onChange, saveQuantity]
  );

  // Leaving the stepper saves at once rather than waiting out the delay. The debounced change
  // that follows carries the same qty, which saveQuantity skips. Moving between the stepper's
  // buttons and input is ignored.
  const handleStepperBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      const stepper = e.currentTarget;
      if (e.relatedTarget instanceof Node && stepper.contains(e.relatedTarget)) {
        return;
      }

      const input = stepper.querySelector('input');
      const parsed = input ? intl.parseNumber(input.value) : Number(value);
      const qty = parsed == null || !Number.isFinite(parsed) ? 0 : parsed;

      saveQuantity(String(dataItem.productKey), clampQty(qty));
    },
    [dataItem.productKey, saveQuantity, intl, value]
  );

  return (
    <GridCellBase
      {...props}
      rowSelectionEnabled={false}
      className="!p-0 w-[100%]"
      title={value.toString()}
    >
      <div ref={stepperRef} className="w-[100%]" onBlur={handleStepperBlur}>
        <NumberStepper
          value={value}
          onChange={handleQuantityChange}
          debounceMs={QTY_AUTO_SAVE_DELAY_MS}
          className="w-[100%]"
        />
      </div>
    </GridCellBase>
  );
};

export default ShoppingCartQtyCell;
